import { isEmpty } from "lodash";
import {
  Button,
  Flex,
  Modal,
  H3,
  Box,
  Small,
  Select,
  TextInput,
  Notification,
} from "orcs-design-system";
import styled from "styled-components";

import ErrorNotification from "src/components/ErrorNotification";
import { RichTextContentViewer } from "src/shared/RichTextContent/RichTextContent";
import TagAttributesFields, {
  ParentTagsSection,
  UniqueAttributesSection,
} from "../TagAttributesFields";

const DetailsWrapper = styled(Box)`
  overflow-y: auto;
  max-height: 150px;
`;

const CreateNewTagTypeColumn = (p) => (
  <Box mr="r" width="50%">
    <Flex flexDirection="column" gap="r">
      <Select
        inputId="tag-type-selector"
        data-testid="tag-type-selector"
        label={
          isEmpty(p.selectedTag)
            ? "Select what type of tag you would like to create:"
            : "Tag type"
        }
        onChange={p.handleTagTypeChange}
        value={p.getSelectedTagOption()}
        options={p.tagTypeOptions}
      />
      {p.hasDetails ? (
        <>
          <Small mb="xs" display="block">
            Tag type description
          </Small>
          <DetailsWrapper
            p="s"
            bg="appBackground"
            boxBorder="default"
            borderRadius={2}
          >
            <RichTextContentViewer content={p.parsedDetails} fontSize="14px" />
          </DetailsWrapper>
        </>
      ) : (
        <TextInput
          fullWidth
          disabled
          label="Tag type description"
          placeholder="No description has been set for this tag type."
        />
      )}
      {!p.hasNoValueAttrs && (
        <TextInput
          name="tag-display-value"
          type="text"
          id="name"
          label="Tag display value"
          fullWidth
          value={p.displayValue}
          disabled
        />
      )}
      <TagAttributesFields
        attrTypeValues={p.readonlyAttrTypes}
        updatedAttributes={p.updatedAttributes}
        onAttrValueChange={p.onAttrValueChange}
        onSelectAttrValue={p.onSelectAttrValue}
        onDateChange={p.onDateChange}
        featureFlags={p.featureFlags}
        tagTypesConfig={p.tagTypesConfig}
        editMode
      />
    </Flex>
  </Box>
);

const CreateNewTagAttributesColumn = (p) => (
  <Box width="50%">
    {p.hasNoValueAttrs && p.selectedTag && (
      <Box mb="r">
        <TextInput
          type="text"
          id="display-value-input"
          label="Tag display value"
          data-testid="displayValueInput"
          placeholder="Not set"
          fullWidth
          mandatory
          value={p.displayValue}
          maxLength={120}
          onChange={p.onDisplayValueChange}
        />
      </Box>
    )}
    <TagAttributesFields
      attrTypeValues={p.editableAttrTypes}
      updatedAttributes={p.updatedAttributes}
      onAttrValueChange={p.onAttrValueChange}
      onSelectAttrValue={p.onSelectAttrValue}
      onDateChange={p.onDateChange}
      fieldToBeFocused={p.fieldToBeFocused}
      fieldsToShowInvalidity={p.fieldsToShowInvalidity}
      featureFlags={p.featureFlags}
      tagTypesConfig={p.tagTypesConfig}
      editMode
    />
  </Box>
);

const CreateNewTagModalBody = (p) => (
  <>
    <Flex width="100%" p="xs" pt="r" flexDirection="column">
      {p.validationError && (
        <Notification
          closable={false}
          variant="error"
          icon="exclamation-triangle"
          mb="r"
        >
          {p.validationError.message}
        </Notification>
      )}
      <Flex width="100%">
        <CreateNewTagTypeColumn {...p} />
        <CreateNewTagAttributesColumn {...p} />
        {p.error && (
          <ErrorNotification message={p.error?.message} error={p.error} />
        )}
      </Flex>
    </Flex>
    {!isEmpty(p.uniqueAttrTypes) && (
      <UniqueAttributesSection
        attrTypeValues={p.uniqueAttrTypes}
        updatedAttributes={p.updatedAttributes}
        onAttrValueChange={p.onAttrValueChange}
        onSelectAttrValue={p.onSelectAttrValue}
        onDateChange={p.onDateChange}
        fieldToBeFocused={p.fieldToBeFocused}
        fieldsToShowInvalidity={p.fieldsToShowInvalidity}
        featureFlags={p.featureFlags}
        tagTypesConfig={p.tagTypesConfig}
        editMode
      />
    )}
    {p.parentTag && p.parentTag.length > 0 && (
      <ParentTagsSection
        attrTypeValues={p.parentTag}
        updatedAttributes={p.updatedAttributes}
        onAttrValueChange={p.onAttrValueChange}
        onSelectAttrValue={p.onSelectAttrValue}
        onDateChange={p.onDateChange}
        fieldToBeFocused={p.fieldToBeFocused}
        fieldsToShowInvalidity={p.fieldsToShowInvalidity}
        featureFlags={p.featureFlags}
        tagTypesConfig={p.tagTypesConfig}
        editMode
      />
    )}
  </>
);

export const CreateNewTagModalLayout = (p) => (
  <Modal
    width={["90vw", "90vw", "90vw", "80vw", "70vw", "50vw"]}
    height={["80vh", "80vh", "80vh", "80vh", "60vh"]}
    maxWidth="90vw"
    maxHeight="90vh"
    visible
    onClose={(e) => {
      if (e) {
        p.onClose();
      }
    }}
    headerContent={
      <Flex px="xs">
        <Box width="50%">
          <H3>Tag details</H3>
        </Box>
        <Box width="50%">
          <H3 ml="19px">Tag attributes</H3>
        </Box>
      </Flex>
    }
    footerContent={
      <Flex pl="4px">
        <Button
          onClick={p.onSave}
          isLoading={p.isSaving}
          disabled={p.isSaving}
          variant={p.isSaving ? "disabled" : "success"}
          mr="s"
        >
          Save
        </Button>
        <Button onClick={p.onClose} variant="ghost">
          Close
        </Button>
      </Flex>
    }
  >
    <CreateNewTagModalBody {...p} />
  </Modal>
);
